import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../config';
import Spinner from './Spinner';

function LoadTemplateModal({ token, onLoad, onClose }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/form-templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      } else {
        setError('Impossibile caricare i modelli');
      }
    } catch (err) {
      setError('Errore di connessione');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleLoadTemplate = (template) => {
    onLoad(template.schema);
    onClose();
  };

  const handleStartEdit = (template) => {
    setEditingId(template.id);
    setEditName(template.name);
  };

  const handleSaveEdit = async (templateId) => {
    if (!editName.trim()) return;

    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/form-templates/${templateId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: editName.trim() })
      });

      if (response.ok) {
        setTemplates(templates.map(t =>
          t.id === templateId ? { ...t, name: editName.trim() } : t
        ));
        setEditingId(null);
        setEditName('');
      } else {
        const data = await response.json();
        setError(data.detail || 'Errore durante il salvataggio');
      }
    } catch (err) {
      setError('Errore di connessione');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (templateId) => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/form-templates/${templateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setTemplates(templates.filter(t => t.id !== templateId));
        setDeleteConfirmId(null);
      } else {
        const data = await response.json();
        setError(data.detail || "Errore durante l'eliminazione");
      }
    } catch (err) {
      setError('Errore di connessione');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-800">Carica Modello</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
              {error}
              <button
                onClick={() => setError('')}
                className="float-right text-red-500 hover:text-red-700"
              >
                &times;
              </button>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-8">
              <Spinner size="lg" color="blue" />
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>Nessun modello salvato</p>
              <p className="text-sm mt-2">Crea un modulo e salvalo come modello per vederlo qui.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition"
                >
                  {editingId === template.id ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveEdit(template.id);
                          if (e.key === 'Escape') { setEditingId(null); setEditName(''); }
                        }}
                      />
                      <button
                        onClick={() => handleSaveEdit(template.id)}
                        disabled={actionLoading}
                        className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                      >
                        Salva
                      </button>
                      <button
                        onClick={() => { setEditingId(null); setEditName(''); }}
                        className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                      >
                        Annulla
                      </button>
                    </div>
                  ) : deleteConfirmId === template.id ? (
                    <div className="flex items-center justify-between">
                      <span className="text-red-600">Confermi eliminazione?</span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleDelete(template.id)}
                          disabled={actionLoading}
                          className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                        >
                          Elimina
                        </button>
                        <button
                          onClick={() => setDeleteConfirmId(null)}
                          className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                        >
                          Annulla
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium text-gray-800">{template.name}</h3>
                        <p className="text-sm text-gray-500">
                          {template.schema.length} camp{template.schema.length === 1 ? 'o' : 'i'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleLoadTemplate(template)}
                          className="px-4 py-2 text-white rounded-lg transition font-medium text-sm"
                          style={{ backgroundColor: '#0E60C6' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#0B4FA3'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#0E60C6'}
                        >
                          Carica
                        </button>
                        <button
                          onClick={() => handleStartEdit(template)}
                          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                          title="Modifica nome"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => setDeleteConfirmId(template.id)}
                          className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg"
                          title="Elimina modello"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
          >
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
}

export default LoadTemplateModal;
